import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import Avatar from '@mui/material/Avatar';
import { useAppSelector } from 'hooks';
import { GenericError, If, ListDropDown } from 'components';
import routesPath from 'routes/RoutesPath';
import { useCommunicationsControl } from 'containers/communications';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { LoadingSpinnerAnimation } from 'assets/animations';
import { ArrowRightIcon, LineGraphIcon } from 'assets/icons';
import { timelineFilterOptions } from './constants';
import { DurationDropdownOptions } from '../types';
import { useLazyGetJobCountSummaryQuery } from '../api';
import { getDatesAsPerFilterForJobs } from 'utils/commonUtils';
import DateUtils from 'utils/dateUtils';

const JobsCountSummaryWidget: React.FC = () => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const navigate = useNavigate();
  const { allowAction: allowJobCreate } = useCommunicationsControl('JOB_CREATE');
  const [getJobCountSummary, getJobCountSummaryState] = useLazyGetJobCountSummaryQuery();
  const [selectedDurationForJobs, setSelectedDurationForJobs] = useState(timelineFilterOptions[0]);

  const {
    isFetching: isFetchingJobCountSummary,
    data: getJobCountSummaryResponse,
    isSuccess: getJobCountSummarySuccess,
    isError: isJobCountSummaryError,
    isUninitialized: isJobCountSummaryUninitialized
  } = getJobCountSummaryState;

  const jobCount = getJobCountSummaryResponse?.data?.count || 0;
  const jobMembersList = getJobCountSummaryResponse?.data?.memberList || [];

  useEffect(() => {
    if (currentTeam?.id) {
      const startDate = getDatesAsPerFilterForJobs(selectedDurationForJobs.name).startDate;
      const endDate = getDatesAsPerFilterForJobs(selectedDurationForJobs.name).endDate;
      getJobCountSummary({
        teamId: currentTeam?.id,
        startDate: DateUtils.startDateToUTCString(startDate),
        endDate: DateUtils.endDateToUTCString(endDate)
      });
    }
  }, [currentTeam, selectedDurationForJobs]);

  const handleFilterSelectEmptyData = () => {
    switch (selectedDurationForJobs.name) {
      case DurationDropdownOptions.TODAY:
        return 'No jobs yet today';
      case DurationDropdownOptions.THIS_MONTH:
        return 'No jobs in this month';
      case DurationDropdownOptions.LAST_MONTH:
        return 'No jobs in last month';
      case DurationDropdownOptions.THIS_YEAR:
        return 'No jobs in this year';
      default:
        return 'No jobs yet today';
    }
  };

  return (
    <div className="w-full pt-5 lg:w-1/2 1439px:pt-10">
      <div className="h-[374px] shrink-0 rounded-[4px] border border-borderGray p-5">
        <div className="flex w-full justify-between text-xl font-bold leading-6 text-primaryText">
          Jobs completed by team member
          <div>
            <ListDropDown
              id="job-count-summary-filter"
              buttonStyles="rounded-[6px] border border-borderGray pl-2 py-1 text-[15px] 
              text-headingGray font-normal w-[112px]"
              optionStyles="min-w-[112px] pb-3"
              itemStyle="text-sbase leading-5 font-normal pl-2 pt-3 pr-0 pb-0"
              items={timelineFilterOptions}
              selectedItem={selectedDurationForJobs}
              setSelectedItem={value => setSelectedDurationForJobs(value)}
              iconStyle="right-1"
            />
          </div>
        </div>
        <If condition={getJobCountSummarySuccess && !isFetchingJobCountSummary && Boolean(jobCount)}>
          <div className="pt-1.5 pb-6 text-xl font-bold leading-6 text-tertiaryText">
            {`${jobCount} ${jobCount === 1 ? 'job' : 'jobs'}`}
          </div>
          <div className="w-full overflow-y-auto">
            <div
              className="customNormalScroll box-border flex max-h-60 w-full flex-col gap-5 
					    overflow-y-auto overflow-x-hidden">
              <div className="mr-4 flex flex-col gap-5">
                {jobMembersList?.map(member => (
                  <div className="flex items-center gap-4" key={member.id}>
                    <Avatar src={member?.profilePictureURL} />
                    <div
                      className="flex w-full flex-col gap-3 text-sbase font-semibold 
							        leading-[18px] tracking-[-0.3px] text-primaryText">
                      {member.name}
                      <div className="relative h-3 w-full">
                        <div className="absolute h-[1px] w-full bg-[#ECF1F7]">
                          <div
                            className="absolute bottom-[-1px] h-1 rounded-md bg-[#A4D7FA]"
                            style={{ width: `${(member.count / jobCount) * 100 || 0}%` }}></div>
                        </div>
                      </div>
                    </div>
                    <div className="text-sbase font-semibold leading-[18px] tracking-[-0.3px] text-primaryText">
                      {member.count}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </If>
        <If condition={getJobCountSummarySuccess && !jobCount && !isFetchingJobCountSummary}>
          <div className="rotateChild flex h-[294px] flex-col items-center justify-center gap-2">
            <LineGraphIcon />
            <div className="text-sbase font-semibold text-accentText">{handleFilterSelectEmptyData()}</div>
            <div
              onClick={() => {
                if (!allowJobCreate()) return;
                navigate(routesPath.CREATE_JOB);
              }}
              className="flex cursor-pointer items-center gap-2">
              <div id="create-new-job/jobs-count-summary" className="text-px13 font-semibold text-secondary">
                Create new job
              </div>
              <ArrowRightIcon />
            </div>
          </div>
        </If>
        <If condition={isFetchingJobCountSummary || isJobCountSummaryUninitialized}>
          <div className="m-auto flex h-[294px] items-center justify-center">
            <Lottie className="h-20 w-20" animationData={LoadingSpinnerAnimation} loop={true} />
          </div>
        </If>
        <If condition={isJobCountSummaryError && !isFetchingJobCountSummary && !isJobCountSummaryUninitialized}>
          <GenericError showBackButton={false} />
        </If>
      </div>
    </div>
  );
};

export default JobsCountSummaryWidget;
